import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, ChevronDown, Download, FileText, Pencil, Search, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { CollapsibleCard } from "@/components/ui/collapsible-card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FieldGroup, FormGrid, Section } from "@/components/ui/section";
import {
  callFeasibility,
  exportFeasibility,
  fetchBuildingTypes,
  fetchMunicipalities,
  gradivostChatOutcome,
  triggerDownload,
} from "@/api/client";
import type { GradivostChatResponse } from "@/api/schemas";
import {
  FeasibilityResult,
  outcomeMeta,
  toFeasibilitySummary,
} from "@/features/feasibility/FeasibilityResult";
import { buildingTypeLabel } from "@/features/feasibility/FeasibilityPanel";
import { GradivostChat } from "@/features/feasibility/GradivostChat";
import { ContextChips } from "@/features/feasibility/ContextChips";
import { ParcelDocDrop } from "@/features/feasibility/ParcelDocDrop";
import { ownersSummary } from "@/features/feasibility/parcelDoc";
import { cn } from "@/lib/utils";
import {
  chatToFormValues,
  isReadyToRun,
} from "@/features/feasibility/chatToFormValues";
import { useGradivostChatStore } from "@/features/feasibility/useGradivostChatStore";
import {
  chatToProjectData,
  projectNameFromChat,
  toExportPayload,
  verdictSignature,
} from "@/features/feasibility/verdictSession";
import {
  useProjectStore,
  type FeasibilityValues,
} from "@/features/projects/useProjectStore";

// Općina NAMJERNO prazna — tihi default („Opatija") je davao verdikt za
// krivu općinu korisniku koji ne skrola; postavlja je chat, Radar handoff
// ili korisnik ručno.
const DEFAULT_VALUES: FeasibilityValues = {
  building_type: "obiteljska_kuca",
  building_subtype: "",
  context: "",
  designation: "",
  in_city_center: false,
  work_type: "novogradnja",
  municipality_code: "",
};

// Most u projekt (Mapa 1 docgen) smije se ponuditi SAMO za GOTOV, riješen odgovor
// — ne za "Potrebna ručna provjera" / "Nedovoljno podataka" / "Postoje prepreke".
// U Mapu 1 se prenosi eventualno provjeren, zaključen verdikt.
const BRIDGEABLE_OUTCOMES = new Set(["ALLOW", "ALLOW_WITH_CONDITIONS"]);

/**
 * P4 (light) — standalone feasibility quick-check. A second door from the
 * landing for the "can I build here?" job that comes long before writing Mapa 1:
 * minimal input → verdict (with article-cited parameter limits) → optional
 * shareable PDF, and a bridge to start a full project seeded from the answer.
 *
 * Ephemeral by design: nothing is saved until the architect chooses "continue
 * into project" — so checking a parcel you may never build leaves no clutter.
 * Reuses the same engine (callFeasibility) and verdict UI (FeasibilityResult)
 * as the in-page panel, so the two never diverge.
 */
export function FeasibilityCheckPage() {
  const navigate = useNavigate();
  const newProject = useProjectStore((s) => s.newProject);
  const upsert = useProjectStore((s) => s.upsert);

  // Verdikt živi u chat sesiji (persist) — reload ga vraća umjesto da
  // korisnik ponovno plati ~50 s za isti odgovor (revizija P1-5).
  const summary = useGradivostChatStore((s) => s.verdict);
  const verdictExport = useGradivostChatStore((s) => s.verdictExport);
  const verdictKey = useGradivostChatStore((s) => s.verdictKey);
  const setVerdict = useGradivostChatStore((s) => s.setVerdict);
  const parcelDoc = useGradivostChatStore((s) => s.parcelDoc);

  // UX krug 1 — forma je sklopivi „fino podešavanje" fallback ispod chata;
  // chatFields pamti koja je polja postavio razgovor (amber provenijencija).
  const [formOpen, setFormOpen] = useState(false);
  const [chatFields, setChatFields] = useState<Array<keyof FeasibilityValues>>([]);
  // Čist start: cijela prošla sesija (razgovor + kontekst + verdikt) se NE
  // izlijeva na otvaranju (izgledalo je kao zbunjujući demo) — stoji iza
  // „Prikaži". Prva interakcija ili svježa provjera je otkriju.
  const [sessionActive, setSessionActive] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FeasibilityValues>({
    defaultValues: DEFAULT_VALUES,
  });

  const typesQuery = useQuery({ queryKey: ["building-types"], queryFn: fetchBuildingTypes });
  const muniQuery = useQuery({ queryKey: ["municipalities"], queryFn: fetchMunicipalities });

  // Keep the building-type select valid against the live enum.
  useEffect(() => {
    const types = typesQuery.data;
    if (types?.length && !types.includes(getValues("building_type"))) {
      setValue("building_type", types[0]);
    }
  }, [typesQuery.data, getValues, setValue]);

  // G2: chat log — verdikt se fire-and-forget dopisuje u gradivost_query_log
  // (Zakon #6) čim postojeći motor odgovori.
  const chatLogId = useRef<number | null>(null);

  // "Nova provjera" mora odbaciti i provjeru koja je još u letu — inače
  // stari rezultat sleti u svježu sesiju (revizija P1-5).
  const runEpoch = useRef(0);

  const mutation = useMutation({
    mutationFn: async (values: FeasibilityValues) => {
      const epoch = runEpoch.current;
      return { data: await callFeasibility(values), epoch };
    },
    onSuccess: ({ data, epoch }, values) => {
      // Sesija je u međuvremenu resetirana → rezultat pripada prošlom
      // razgovoru; odbaci ga umjesto da sleti u svježu provjeru.
      if (epoch !== runEpoch.current) return;
      const parsedNow = useGradivostChatStore.getState().lastResponse?.parsed ?? null;
      setVerdict(
        toFeasibilitySummary(data),
        toExportPayload(data),
        verdictSignature(values, parsedNow),
      );
      setSessionActive(true); // svježa provjera → otkrij sesiju
      if (chatLogId.current != null && data.outcome_class) {
        gradivostChatOutcome(chatLogId.current, data.outcome_class);
        chatLogId.current = null;
      }
      window.setTimeout(() => {
        document
          .querySelector('[data-testid="feasibility-result"]')
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    },
  });

  // G2: chat odgovor → hidratacija POSTOJEĆE forme (fill-empty preko
  // trenutnih vrijednosti) + auto-run POSTOJEĆE provjere kad ništa ne
  // nedostaje — isti šav kao Radar prefill niže.
  function onChatAnswer(resp: GradivostChatResponse, overrides?: { designation?: string }) {
    setSessionActive(true); // korisnik je pitao → otkrij sesiju (čist start gotov)
    const mapped = { ...chatToFormValues(resp), ...(overrides ?? {}) };
    const keys = Object.keys(mapped) as Array<keyof FeasibilityValues>;
    keys.forEach((k) => setValue(k, mapped[k] as never));
    setChatFields(keys);
    chatLogId.current = resp.log_id;
    // Guard: bez općine motor nema plan — chat u tom slučaju ionako
    // postavlja potpitanje, ali klik na zonu-chip ne smije okinuti prazno.
    if ((overrides?.designation || isReadyToRun(resp)) && getValues("municipality_code")) {
      mutation.mutate(getValues());
    }
  }

  // G3: vraćena chat sesija (persist) → hidratiziraj formu da se slaže s
  // chipsima; BEZ auto-runa (verdikt korisnik ponovno traži sam). Jednom.
  const sessionHydrated = useRef(false);
  useEffect(() => {
    // čekaj options za Općina select — setValue prije njih se tiho izgubi
    if (sessionHydrated.current || !muniQuery.data) return;
    sessionHydrated.current = true;
    const restored = useGradivostChatStore.getState().lastResponse;
    if (!restored) return;
    const mapped = chatToFormValues(restored);
    const keys = Object.keys(mapped) as Array<keyof FeasibilityValues>;
    keys.forEach((k) => setValue(k, mapped[k] as never));
    setChatFields(keys);
  }, [setValue, muniQuery.data]);

  // Land Radar handoff: zona prosljeđuje predpopunu (tip/podtip/oznaka/općina) kroz
  // location.state → seed forme + auto-pokreni provjeru. Jednom po dolasku.
  const location = useLocation();
  const prefillDone = useRef(false);
  useEffect(() => {
    if (prefillDone.current) return;
    const pf = (location.state as { prefill?: Partial<FeasibilityValues> } | null)?.prefill;
    if (!pf) return;
    prefillDone.current = true;
    const merged = { ...DEFAULT_VALUES, ...pf } as FeasibilityValues;
    (Object.keys(merged) as Array<keyof FeasibilityValues>).forEach((k) =>
      setValue(k, merged[k] as never),
    );
    setSessionActive(true);
    mutation.mutate(merged);
  }, [location.state, setValue, mutation]);

  const exportPdf = useMutation({
    mutationFn: async () => {
      if (!verdictExport) return;
      const blob = await exportFeasibility(verdictExport);
      triggerDownload(blob, "Permitko_nalaz_gradivosti.pdf");
    },
  });

  // Sklopljeni sažetak forme — jedna linija chipova umjesto rastvorenih polja.
  const values = watch();
  const muniName =
    muniQuery.data?.municipalities.find((m) => m.code === values.municipality_code)?.name ?? null;

  // Zastarjeli verdikt: parametri (uklj. česticu iz razgovora) su se
  // promijenili otkad je verdikt izračunat — panel to mora reći, a most u
  // projekt ne smije nositi tuđi odgovor (revizija P1-5).
  const chatParsed = useGradivostChatStore((s) => s.lastResponse?.parsed ?? null);
  const currentKey = verdictSignature(values, chatParsed);
  const verdictStale = !!summary && !!verdictKey && verdictKey !== currentKey;
  // Nudi „Nastavi u projekt" samo za gotov, riješen ishod (v. BRIDGEABLE_OUTCOMES).
  const canBridge =
    !!summary && BRIDGEABLE_OUTCOMES.has((summary.outcome_class ?? "").toUpperCase());

  /** P1-4: plan za k.o. je upravo učitan i aktiviran kroz wizard — upiši
   *  općinu i, ako je sve ostalo poznato, odmah daj verdikt (do sad je
   *  razgovor o Buzetu završavao u slijepoj ulici). */
  function onPlanActivated(code: string) {
    setSessionActive(true);
    setValue("municipality_code", code);
    setChatFields((f) => (f.includes("municipality_code") ? f : [...f, "municipality_code"]));
    const values = { ...getValues(), municipality_code: code };
    if (values.building_type && values.designation) mutation.mutate(values);
  }

  /** Tvrdi reset: razgovor + verdikt + parametri forme, i odbaci provjeru
   *  koja je još u letu. */
  function resetSession() {
    runEpoch.current += 1;
    useGradivostChatStore.getState().clear();
    (Object.keys(DEFAULT_VALUES) as Array<keyof FeasibilityValues>).forEach((k) =>
      setValue(k, DEFAULT_VALUES[k] as never),
    );
    setChatFields([]);
    setFormOpen(false);
    setSessionActive(false);
    chatLogId.current = null;
    mutation.reset();
    exportPdf.reset();
  }
  /** Amber okvir za polja koja je postavio razgovor (konvencija hidratacije). */
  const chatHighlight = (field: keyof FeasibilityValues) =>
    chatFields.includes(field) && "border-amber-400 bg-amber-50/40";

  // Bridge: turn the quick check into a full project, seeded from the answer.
  // P1-6: sve što je razgovor već pročitao iz službenog katastra (k.č.,
  // k.o., površina, lokacija) putuje s projektom — inače bi Donesi tražio
  // ručni unos upravo tih podataka, u trenutku najvećeg povjerenja.
  function continueToProject() {
    if (!summary || verdictStale || !canBridge) return;
    const zoneMemo = useGradivostChatStore.getState().zoneMemo;
    const parcelArea =
      zoneMemo && zoneMemo.kc === (chatParsed?.kc ?? "") ? zoneMemo.parcelAreaM2 : null;
    const naselje = zoneMemo?.zones?.find((z) => z.oznaka === values.designation)?.naselje;
    const carried = chatToProjectData(chatParsed, parcelArea, naselje ?? null);
    const name = projectNameFromChat(chatParsed);

    // G1: ako je čestica došla iz ZK izvatka, prenesi i podatke koje samo ZK
    // pouzdano nosi (površina, suvlasnici kao prijedlog investitora). Teret
    // ostaje readiness-nalaz u modulu gradivosti — nije polje projekta.
    const parcelDoc = useGradivostChatStore.getState().parcelDoc;
    if (parcelDoc?.facts) {
      const f = parcelDoc.facts;
      if (f.povrsinaM2 != null) carried.cestica_povrsina_m2 = f.povrsinaM2;
      const owners = ownersSummary(f.vlasnici);
      if (owners && !carried.investitor_name) carried.investitor_name = owners;
    }

    const id = newProject();
    const current = useProjectStore.getState().projects.find((p) => p.id === id);
    if (current) {
      upsert({
        ...current,
        ...(name ? { name } : {}),
        feasibility: getValues(),
        feasibilityResult: summary,
        projectData: { ...(current.projectData ?? {}), ...carried },
        // No sources yet — the generation flow starts at Donesi.
        currentStep: 1,
      });
    }
    navigate(`/project/${id}/donesi`);
  }

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="inline-flex items-center gap-2 text-[1.3rem] font-bold text-stone-800">
          <ShieldCheck className="h-5 w-5 text-ind-600" />
          Provjera gradivosti
        </h1>
        <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
          <ArrowLeft className="h-4 w-4" />
          Natrag
        </Button>
      </div>
      <p className="mb-5 text-[0.9rem] text-stone-500">
        Upiši k.č. i općinu — ili pitaj slobodno — i dobiješ verdikt smije li se
        graditi, s limitima plana i citatom članka. Bez učitavanja dokumenata;
        ništa se ne sprema dok ne nastaviš u projekt.
      </p>

      <GradivostChat
        onAnswer={onChatAnswer}
        engineRunning={mutation.isPending}
        onReset={resetSession}
        showHistory={sessionActive}
        verdict={
          summary && sessionActive && !verdictStale
            ? {
                label: outcomeMeta(summary.outcome_class).label,
                kind: outcomeMeta(summary.outcome_class).kind,
              }
            : null
        }
      />

      {/* Jedini izvor istine za kontekst čestice — jedan red (bez duplih chipova).
          Skriven na čistom praznom otvoru; otkriva se prvom interakcijom. */}
      {sessionActive && (
        <ContextChips
          values={values}
          muniName={muniName}
          onAnswer={onChatAnswer}
          onPlanActivated={onPlanActivated}
          onEditManual={() => setFormOpen(true)}
        />
      )}

      {/* G1 — koš dokumenata čestice (ZK izvadak): progresivno otkriven,
          hidratira istu formu kao chat + cross-check nalazi. */}
      <CollapsibleCard
        className="mb-3"
        icon={<FileText className="h-4 w-4 text-stone-400" />}
        title="Dokument čestice (ZK izvadak)"
        summary={
          parcelDoc?.facts ? (
            <span className="rounded bg-stone-100 px-1.5 py-0.5 text-[0.72rem] text-stone-600">
              učitano
            </span>
          ) : (
            <span className="text-[0.72rem] text-stone-400">
              nije obavezno — očita česticu, površinu i terete
            </span>
          )
        }
      >
        <ParcelDocDrop onAnswer={onChatAnswer} />
      </CollapsibleCard>

      <Card>
        <CardHeader className="cursor-pointer select-none" onClick={() => setFormOpen((v) => !v)}>
          <CardTitle>Parametri provjere</CardTitle>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-expanded={formOpen}
            onClick={(e) => {
              e.stopPropagation();
              setFormOpen((v) => !v);
            }}
          >
            {formOpen ? (
              <>
                Sakrij
                <ChevronDown className="h-4 w-4 rotate-180" />
              </>
            ) : (
              <>
                <Pencil className="h-3.5 w-3.5" />
                Uredi ručno
              </>
            )}
          </Button>
        </CardHeader>
        <CardBody>
          {!formOpen && (
            <p className="text-[0.78rem] text-stone-400">
              Tip, općina, zona i ostali parametri — postavlja ih razgovor iznad
              (vidljivi kao chipovi), ili ih ovdje urediš ručno.
            </p>
          )}
          {formOpen && (
          <form onSubmit={(e) => void handleSubmit((v) => mutation.mutate(v))(e)}>
            {chatFields.length > 0 && (
              <p className="mb-3 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-[0.78rem] text-amber-800">
                Žuto označena polja postavio je razgovor — provjeri ih prije
                ponovne provjere.
              </p>
            )}
            <Section title="Tip projekta">
              <FormGrid cols={2}>
                <FieldGroup>
                  <Label htmlFor="fc_building_type" required>Tip građevine</Label>
                  <select
                    id="fc_building_type"
                    className={cn(
                      "rounded-md border border-stone-300 bg-white px-3 py-[0.55rem] text-[0.88rem] text-stone-800 focus-visible:border-ind-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ind-500/15",
                      chatHighlight("building_type"),
                    )}
                    {...register("building_type")}
                  >
                    {(typesQuery.data ?? []).map((t) => (
                      <option key={t} value={t}>{buildingTypeLabel(t)}</option>
                    ))}
                  </select>
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="fc_building_subtype">Podtip</Label>
                  <Input id="fc_building_subtype" {...register("building_subtype")} />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="fc_work_type">Tip zahvata</Label>
                  <select
                    id="fc_work_type"
                    className={cn(
                      "rounded-md border border-stone-300 bg-white px-3 py-[0.55rem] text-[0.88rem]",
                      chatHighlight("work_type"),
                    )}
                    {...register("work_type")}
                  >
                    <option value="novogradnja">Novogradnja</option>
                    <option value="rekonstrukcija">Rekonstrukcija</option>
                    <option value="dogradnja">Dogradnja</option>
                  </select>
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="fc_municipality_code" required>Općina</Label>
                  <select
                    id="fc_municipality_code"
                    className={cn(
                      "rounded-md border border-stone-300 bg-white px-3 py-[0.55rem] text-[0.88rem]",
                      chatHighlight("municipality_code"),
                    )}
                    {...register("municipality_code", {
                      required: "Odaberi općinu — bez nje nema plana za provjeru.",
                    })}
                  >
                    <option value="">— odaberi —</option>
                    {(muniQuery.data?.municipalities ?? []).map((m) => (
                      <option key={m.code} value={m.code}>{m.name}</option>
                    ))}
                  </select>
                  {errors.municipality_code && (
                    <p className="mt-1 text-[0.78rem] text-red-600">
                      {errors.municipality_code.message}
                    </p>
                  )}
                </FieldGroup>
              </FormGrid>
            </Section>

            <Section title="Lokacija i kontekst">
              <FormGrid cols={2}>
                <FieldGroup>
                  <Label htmlFor="fc_designation">Zona / namjena</Label>
                  <Input
                    id="fc_designation"
                    placeholder="npr. S1, M1, D1"
                    className={cn(chatHighlight("designation"))}
                    {...register("designation")}
                  />
                </FieldGroup>
                <FieldGroup>
                  <Label className="mt-7" htmlFor="fc_in_city_center">
                    <input id="fc_in_city_center" type="checkbox" className="mr-2" {...register("in_city_center")} />
                    U gradskom središtu
                  </Label>
                </FieldGroup>
                <FieldGroup full>
                  <Label htmlFor="fc_context">Slobodan opis</Label>
                  <Textarea
                    id="fc_context"
                    rows={3}
                    placeholder="Npr. KIG 0.4, KISN 0.8, Po+P+1, visina v=6.5m, V=8.5m"
                    className={cn(chatHighlight("context"))}
                    {...register("context")}
                  />
                </FieldGroup>
              </FormGrid>
            </Section>

            <div className="flex justify-end">
              <Button type="submit" disabled={mutation.isPending}>
                <Search className="h-4 w-4" />
                {mutation.isPending
                  ? "Analiziram…"
                  : summary
                    ? "Ponovi provjeru"
                    : "Provjeri mogućnosti gradnje"}
              </Button>
            </div>
          </form>
          )}
        </CardBody>
      </Card>

      {mutation.error instanceof Error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-[0.85rem] text-red-700">
          Greška: {mutation.error.message}
        </div>
      )}

      {/* Čist start: cijela prošla sesija stoji iza „Prikaži". */}
      {summary && !sessionActive && (
        <div className="mt-6 flex flex-wrap items-center gap-3 rounded-lg border border-stone-200 bg-white px-4 py-3">
          <span className="text-[0.85rem] text-stone-600">
            Prethodna provjera:{" "}
            <span className="font-semibold text-stone-800">
              {outcomeMeta(summary.outcome_class).label}
            </span>
            {summary.municipality_name && (
              <span className="text-stone-400"> · {summary.municipality_name}</span>
            )}
          </span>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setSessionActive(true)}>
              Prikaži
            </Button>
            <Button variant="ghost" size="sm" onClick={resetSession}>
              Nova provjera
            </Button>
          </div>
        </div>
      )}

      {summary && sessionActive && (
        <>
          {verdictStale && (
            <div
              data-testid="verdict-stale"
              className="mt-6 rounded-md border border-stone-300 bg-stone-100 px-4 py-3 text-[0.85rem] text-stone-600"
            >
              <span className="font-semibold">Verdikt je zastario.</span> Vrijedi
              za prethodne parametre — pokreni provjeru ponovno da vidiš odgovor
              za ono što sada gledaš.
            </div>
          )}
          <div className={cn(verdictStale && "opacity-60")}>
            <FeasibilityResult
              summary={summary}
              onContinue={verdictStale || !canBridge ? undefined : continueToProject}
            />
          <div className="mt-3 flex items-center justify-end gap-3">
            {exportPdf.isError && (
              <p className="text-[0.78rem] text-red-700">
                PDF nalaz trenutačno nije moguće izraditi — pokušaj ponovno ili
                nastavi bez njega.
              </p>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportPdf.mutate()}
              disabled={exportPdf.isPending || !verdictExport}
            >
              <Download className="h-3.5 w-3.5" />
              {exportPdf.isPending ? "Pripremam…" : "Preuzmi nalaz (PDF)"}
            </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
